import { Digest } from '../../global/digest';
import { browser } from '../../global/browser';
import {
    appendModalControllerIfRequired,
    makePopoverHandler,
    makePopoverMaxHeightHandler,
    fitsHorizontally,
} from '../../global/helpers';
import { createElement } from '../../global/render-api';
import { UIElement } from '../ui-element';
import { UIModal } from '../modal/ui-modal';
import { html, element } from '../../global/template-literals';
import {
    hide,
    show,
    setInnerText,
    addOutsideClickHandler,
} from '../../global/ui-helpers';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import type { AttributeValue, ObservedAttributes } from '../ui-element.type';
import type { NullOr, UndefinedOr } from '../../global/utils.type';
import type {
    UIChipLayout,
    UIChipLayouts,
    UIChipOffsets,
} from './ui-chip.type';
import type {
    UIModalWidth,
    UIModalWidthsCompact,
} from '../modal/ui-modal.types';
import styles from './ui-chip.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIChip
 * @element ui-chip
 * @classdesc Represents a class for <code>ui-chip</code> element.
 * Chips are used for filtering and invoking actions.
 * @fires popover-open
 * @fires popover-close
 * @property {string} label {@attr label} Label of the chip.
 * @property {UIIconGlyph} [glyph] {@attr glyph} Glyph of the chip.
 * @property {("filter" | "button" | "menu")} [layout="filter"] {@attr layout}
 * Layout of the chip. "popover" is a legacy alias for "menu".
 * @property {boolean} [expanded] {@attr expanded} Indicates if menu popover is expanded.
 * @property {boolean} [inverted] {@attr inverted} Toggles chip design to inverted one (transparent background).
 * @property {boolean} [bottomSheet] {@attr bottom-sheet} Allows the popover to open as a bottom sheet on mobile.
 * @property {UIModalWidthCompact} [popoverWidth="small"] {@attr popover-width} Fixed width of the popover.
 * @property {HTMLInputElement} control Control element.
 * @slot
 * @example
 * <ui-chip label="Chip"></ui-chip>
 * @example
 * <ui-chip label="Radio chip">
 *   <input type="radio" name="radio-chip-group">
 * </ui-chip>
 * @example
 * <ui-chip label="Button" layout="button"></ui-chip>
 * @example
 * <ui-chip label="Menu" layout="menu">
 *   <template>Menu content</template>
 * </ui-chip>
 * @example
 * <ui-chip label="Menu wide" layout="menu" popover-width="medium">
 *   <template>Wide menu content</template>
 * </ui-chip>
 */
@Component({ tag: 'ui-chip', styles })
export class UIChip extends UIElement {
    /**
     * Layouts of the chip.
     */
    static readonly Layout: UIChipLayouts = {
        Filter: 'filter',
        Button: 'button',
        Menu: 'menu',
    };

    /**
     * Popover width presets.
     * @returns {{Small: number, Medium: number}}
     */
    static get PopoverWidth(): UIModalWidthsCompact {
        return {
            Small: 'small',
            Medium: 'medium',
        };
    }

    /**
     * @private
     */
    private static readonly Offset: UIChipOffsets = {
        Vertical: 5,
        Horizontal: 0,
    } as const;

    @Attribute(String) label?: string;
    @Attribute(String) glyph?: string;
    @Attribute(String) layout: UIChipLayout = UIChip.Layout.Filter;
    @Attribute(Boolean) expanded?: boolean;
    @Attribute(Boolean) inverted?: boolean;
    @Attribute(Boolean) bottomSheet?: boolean;
    @Attribute(String) popoverWidth: string = UIChip.PopoverWidth.Small;

    @Child('.ui-chip__control') control!: HTMLInputElement;

    /** @private */
    private _detachedPopover: NullOr<HTMLElement> = null;
    /** @private */
    private outsideClickRemover?: () => void;
    /** @private */
    private resizeHandler?: () => void;

    constructor() {
        super();
        this.onControlClick = this.onControlClick.bind(this);
    }

    /**
     * @type {Array<string>}
     */
    static get observedAttributes(): ObservedAttributes {
        return [
            'layout',
            'label',
            'expanded',
            'glyph',
            'inverted',
            'bottom-sheet',
            'popover-width',
        ];
    }

    get hasPopover(): boolean {
        return this.popoverElement != null;
    }

    get popoverElement(): NullOr<HTMLElement> {
        if (this._detachedPopover) {
            return this._detachedPopover;
        }
        return this.querySelector<HTMLElement>('.ui-chip__popover');
    }

    get template(): DocumentFragment {
        const input = this.querySelector<HTMLInputElement | HTMLButtonElement>(
            ':scope > input, :scope > button'
        );
        const control = createElement({
            element: input || this.buildControlElement(),
            classList: {
                'ui-chip__control': true,
            },
        })!;

        const controlId = control.id;
        if (!controlId) {
            control.id = Digest.randomId();
        }

        return html`
            ${control}
            <label
                for="${control.id}"
                class="ui-chip__label"
            >
                <span class="ui-chip__label-wrapper">
                    ${this.glyph &&
                    html`<ui-icon glyph="${this.glyph}"></ui-icon>`}
                    ${this.layout !== UIChip.Layout.Button &&
                    !this.glyph &&
                    html`<ui-icon glyph="redesign-check"></ui-icon> `}
                    <span class="ui-chip__label-selected-count"></span>
                    ${this.label}
                    ${this.isLayoutMenu() &&
                    html`<ui-icon glyph="redesign-caret-down"></ui-icon> `}
                </span>
            </label>
            ${this.isLayoutMenu() &&
            html`
                <div class="ui-chip__popover -hidden">
                    ${this.querySelector('.ui-chip__popover-content')}
                </div>
            `}
        `;
    }

    /**
     * @type {UIModal | null}
     */
    get modal(): NullOr<UIModal> {
        return this._detachedPopover?.closest('ui-modal') as NullOr<UIModal>;
    }

    /**
     * Sets popover width in pixels.
     * @param {number} value
     */
    setPopoverWidth(value: NullOr<number | string>) {
        const isBottomSheet = this.bottomSheet && browser.mobileMediaMatches();
        if (!this.popoverElement) {
            return;
        }
        if (isBottomSheet) {
            (this.popoverElement.style.width as string | null) = null;
            return;
        }
        (this.popoverElement.style.width as string | null) =
            (value as number) > 0 ? `${value}px` : null;
    }

    /**
     * Builds control element based on layout.
     * @returns {HTMLElement}
     */
    buildControlElement(): HTMLElement {
        switch (this.layout) {
            case UIChip.Layout.Filter:
            case UIChip.Layout.Menu:
                return element`<input type="checkbox" />`;
            case UIChip.Layout.Button:
                return element`<button aria-label="${this.label}"></button>`;
            default:
                return element`<input type="checkbox" />`;
        }
    }

    /**
     * Changes popover state.
     * Dispatches <code>popover-open</code> and <code>popover-close</code> events.
     */
    onChangePopoverState(): void {
        if (this.expanded) {
            this.openDetachedPopover();
            // document.addEventListener('click', this.handleClickOutside);
            this.dispatchCustomEvent('popover-open', {
                popover: this.popoverElement,
            });
            void addOutsideClickHandler(
                this,
                this.handleClickOutside.bind(this) as () => void
            ).then((func) => (this.outsideClickRemover = func));
        } else {
            const checkboxes = this.popoverElement?.querySelectorAll('input');
            const checked = [].filter.call(
                checkboxes,
                (checkbox: HTMLInputElement) => checkbox.checked
            );

            this.control.checked = checked.length > 0;
            this.updateClassList({ '-multiselected': checked.length > 0 });
            setInnerText(
                this.querySelector<HTMLElement>(
                    '.ui-chip__label-selected-count'
                )!,
                String(checked.length)
            );

            this.closeDetachedPopover();
            this.outsideClickRemover?.();
            this.dispatchCustomEvent('popover-close', {
                popover: this.popoverElement,
            });
        }
    }

    /**
     * Opens popover.
     */
    openDetachedPopover(): void {
        if (this._detachedPopover) {
            return;
        }
        this._detachedPopover = this.popoverElement;
        show(this.popoverElement!);

        appendModalControllerIfRequired();
        this.dispatchCustomEvent('modal-open', {
            type: UIModal.Type.Menu,
            content: this.popoverElement,
            params: {
                attributes: {
                    'compact': true,
                    'no-bottom-sheet': !this.bottomSheet,
                },
                position: makePopoverHandler(this, {
                    popoverHeight: () => this.popoverElement?.offsetHeight,
                    popoverWidth: () => {
                        const width =
                            UIModal.WindowWidth[
                                this.popoverWidth as UIModalWidth
                            ];
                        const finalWidth = fitsHorizontally(width)
                            ? width
                            : window.innerWidth;
                        this.setPopoverWidth(finalWidth);
                        return this.popoverElement?.offsetWidth;
                    },
                    verticalOffset: UIChip.Offset.Vertical,
                    horizontalOffset: UIChip.Offset.Horizontal,
                }),
                target: this,
                onClose: () => {
                    this.closePopover();
                    this.control.focus();
                },
            },
        });
    }

    /**
     * Closes popover.
     */
    closeDetachedPopover(): void {
        if (!this._detachedPopover) {
            return;
        }
        hide(this.popoverElement!);
        const modal = this.modal; // query once
        if (modal) {
            this._detachedPopover = null;
            modal.close();
        }
    }

    /**
     * Fires callback when user clicked outside of element.
     * @param {Event} event
     * @private
     */
    private handleClickOutside(event: Event): void {
        if (
            !this.expanded ||
            this.popoverElement?.contains(event.target as Node) ||
            this.contains(event.target as Node) ||
            (event.target as HTMLElement)?.closest('ui-modal')
        ) {
            return;
        }
        this.closePopover();
    }

    /**
     * Fires callback when user clicks on control.
     * @param {Event} e
     */
    onControlClick(e: Event): void {
        if (this.hasPopover) {
            e.preventDefault();
            e.stopPropagation();
            this.expanded = !this.expanded;
        }
    }

    /**
     * Closes popover.
     */
    closePopover(): void {
        this.expanded = false;
    }

    /**
     * @inheritDoc
     */
    override observeAttributes(
        name: AttributeValue,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        /* istanbul ignore if */
        if (!this.hydrated) {
            return;
        }

        if (name === 'expanded' && this.isLayoutMenu()) {
            this.onChangePopoverState();
        } else {
            this.rebuildChildren([this.template]);
        }
    }

    /**
     * Checks if the layout opens a menu-type modal.
     * Also matches legacy 'popover' layout for backward compatibility.
     * @private
     * @returns {boolean}
     */
    private isLayoutMenu(): boolean {
        return (
            this.layout === UIChip.Layout.Menu ||
            (this.layout as string) === 'popover'
        );
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        if (this.isLayoutMenu()) {
            let content: UndefinedOr<DocumentFragment>;
            const popoverTemplate =
                this.querySelector<HTMLTemplateElement>('template');
            if (popoverTemplate) {
                content = popoverTemplate.content;
                popoverTemplate.remove();
            }
            this.appendChild(
                html`<div class="ui-chip__popover-content">${content}</div>`
            );
        }

        this.rebuildChildren([this.template]);
        this.setAttribute('role', 'group');

        const isIconOnly = !!(
            this.glyph &&
            !this.label &&
            !this.isLayoutMenu()
        );
        this.updateClassList({ '-icon-only': isIconOnly });
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.control.addEventListener('click', this.onControlClick);
        if (this.hasPopover && this.expanded) {
            // In case popover is expanded on load, we need to open it after hydration.
            // Otherwise, ui-modal-controller is not ready to listen for events.
            setTimeout(() => {
                if (this.expanded) {
                    this.onChangePopoverState();
                }
            }, 0);
        }
        if (this.isLayoutMenu()) {
            this.resizeHandler = makePopoverMaxHeightHandler(
                this.popoverElement,
                UIChip.Offset.Vertical
            );
            this.resizeHandler();
        }
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
        }
        this.outsideClickRemover?.();
    }
}
